/**
 * The app's state (docs/08-entscheidungen.md, S1): the server is authoritative, the app holds the
 * last state it loaded. That state is kept in the browser, so menus can be looked at, printed and
 * exported without a network; changing needs the network, and the app says so instead of waiting.
 * The data is fetched again when the app comes to the front, when the network returns, every minute,
 * and after every change; the business's version tells whether anything moved.
 */
import { useSyncExternalStore } from "react";
import type { DrinksFrom } from "@speisekarte/core";
import { DATA_FORMAT } from "@speisekarte/core";
import { update } from "./update.ts";
import type { EntrancePrinted, Exception, Folder, Planned, Schedule, Timing, Lang, Allergen, CardText, Designs, Dish, Menu, OwnPage, PdfSlot, Phrases, Proofs, PublishedMenu, Role, SoldOut } from "@speisekarte/core";

export interface User {
  id: string;
  username: string;
  displayName: string;
  email: string | null;
  /** Confirmed through its link (S28); missing in a session kept by an older app. */
  emailConfirmed?: boolean;
  /** A new address waiting for its link. */
  emailPending?: string | null;
}

export interface Membership {
  id: string;
  name: string;
  role: Role;
}

export interface SignedIn {
  user: User;
  workspaces: Membership[];
}

export interface Settings {
  house: string;
  address: string;
  imprint: string;
  privacy: string;
  footer: string;
  /** Phone and e-mail of the house (S50); missing: none. */
  phone?: string;
  email?: string;
  /** The house's website, Instagram and Facebook (S50); missing: none. */
  website?: string;
  instagram?: string;
  facebook?: string;
  /** The same in one line at the foot of the printed card (S50). */
  linksInPrint?: boolean;
  keepMenus: number;
  kellerlog: string;
  /** Where the numbers of new drinks begin (S22). */
  drinkStart: number;
  /** On the net, the drinks of this menu with the published one's food (S22); empty: its own. */
  drinksMenu: string;
  /** Where the tab „Getränke“ comes from (S59); empty or missing: the menu's own drinks, else KellerLog's. */
  drinksFrom?: DrinksFrom;
  /** On the net, this menu above the published one as its recommendations (S37, L2); empty or missing: none. */
  recommendationsMenu?: string;
  /** The business's own address for the QR code and the stickers (S40), checked by the server; empty or missing: the link. */
  ownUrl?: string;
  /** Further languages of the cards (S36); empty or missing: German only. */
  languages?: Lang[];
  /** „Deutsch prüfen“ (S47); missing: off. */
  proofread?: boolean;
  /** Der Allergenfilter für Gäste (S63); missing: off. */
  allergenFilter?: boolean;
  /** The price list beside the entrance (S42), with the catalogue's prices; empty or missing: none. */
  entranceMenu?: string;
  /** When it was printed last and what it showed (S42). */
  entrancePrinted?: EntrancePrinted | null;
  /** Die Zeitsteuerung (S62): missing or „off“ the app of before. */
  timing?: Timing;
  /** Später ins Netz (S62): the changes waiting for their moment. */
  planned?: Planned[];
  /** Der Wochenplan (S62, Stufe 2), its exceptions and the name of „Sonst“. */
  schedule?: Schedule;
  exceptions?: Exception[];
  otherwiseName?: string;
}

export interface Data {
  version: number;
  workspace: string;
  role: Role;
  dishes: Dish[];
  allergens: Allergen[];
  /** The categories of the business, in their order (S12). */
  categories: string[];
  /** The texts of the catalogue, in their order (S14). */
  texts: CardText[];
  /** The tags of the business (S17), in their order. */
  tags: string[];
  /** The categories and tags of the drinks (S22), in their order. */
  drinkCategories: string[];
  drinkTags: string[];
  menus: Menu[];
  settings: Settings;
  /** With the recommendations above it (S37): which menu, and their own mark. */
  published: (Pick<PublishedMenu, "menu" | "publishedAt"> & { digest: string; lead?: { menu: string; digest: string }; filterReady?: true }) | null;
  /** The tags' symbols (S21), by the tag as in the list. */
  tagIcons: Record<string, string>;
  /** The styles with an own Word template (S21), by file and time. */
  word: { style: string; file: string; at: number }[];
  /** The styles of the menus (S15); null: „Klassisch“ only. */
  designs: Designs | null;
  /** Their version (S55), sent back with a change. */
  designsVersion: number;
  /** The logo by size and time (S13); the picture itself: logo.ts. */
  logo: { width: number; height: number; at: number; file: string } | null;
  /** The dishes sold out now (S39), until when. */
  soldOut: SoldOut[];
  /** „Deutsch prüfen“ (S47): per spot what was checked and the corrections still open. */
  proofs: Proofs;
  /** The house's own pages (S50); missing in data kept before. */
  ownPages?: OwnPage[];
  /** PDF unter festen Adressen (S54), with their addresses. */
  pdfSlots?: PdfSlot[];
  /** The business's own words in further languages (S36, 31.10.2026); missing in data kept before. */
  phrases?: Phrases;
  /** The folders of the weekly plan (S62, Stufe 2) with the mark of their frozen state; the PDFs they lay into links. */
  folders?: (Folder & { published: { menu: string; publishedAt: number; digest: string; lead?: { menu: string; digest: string }; filterReady?: true } | null })[];
  folderPdfs?: { folder: string; slot: string; file: string; menu: string; pages: number; digest: string; lang: string; madeAt: number }[];
}

export interface Session {
  user: User;
  workspaces: Membership[];
  workspaceId: string;
  demo?: boolean;
}

const TIMEOUT_MS = 12_000;
const SESSION_KEY = "speisekarte:session";
/**
 * The data kept under the shape it has (DATA_FORMAT, core): data kept by an older app is then not
 * used, and the first load fetches all of it – otherwise the device would wait for the business to
 * change before it saw a new field („since“ answers „unchanged“), and break on what it lacks (S17).
 */
const dataKey = (workspace: string) => `speisekarte:data:${DATA_FORMAT}:${workspace}`;

export class ApiError extends Error {
  status: number;
  code: string | undefined;
  current: unknown;
  constructor(status: number, message: string, code?: string, current?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.current = current;
  }
}

/** A request to the server; the business is named when one is chosen. */
export async function api<T>(path: string, init: { method?: string; body?: unknown; form?: FormData; pdf?: Blob; timeout?: number } = {}, workspace: string | null = store.session?.workspaceId ?? null): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), init.timeout ?? TIMEOUT_MS);
  // Files (S41) as a form, its boundary set by the browser.
  const headers: Record<string, string> = init.form ? {} : { "content-type": init.pdf ? "application/pdf" : "application/json" };
  if (workspace) headers["x-speisekarte-workspace"] = workspace;
  try {
    const res = await fetch(path, {
      method: init.method ?? (init.body === undefined && !init.form && !init.pdf ? "GET" : "POST"),
      headers,
      credentials: "same-origin",
      signal: controller.signal,
      ...(init.form ? { body: init.form } : init.pdf ? { body: init.pdf } : init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
    });
    const version = res.headers.get("x-speisekarte-version");
    if (version) update.server(version);
    const text = await res.text();
    let body: Record<string, unknown> = {};
    try {
      body = text ? (JSON.parse(text) as Record<string, unknown>) : {};
    } catch {
      if (!res.ok) throw new ApiError(res.status, text.slice(0, 200) || res.statusText);
    }
    if (!res.ok) {
      if (res.status === 401 && store.session) store.signedOutElsewhere();
      throw new ApiError(res.status, typeof body.error === "string" ? body.error : res.statusText, typeof body.code === "string" ? body.code : undefined, body.current);
    }
    return body as T;
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new ApiError(0, "Keine Verbindung zum Server. Ändern geht nur mit Netz.", "offline");
  } finally {
    clearTimeout(timer);
  }
}

export const messageOf = (err: unknown) => (err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err));

const readJson = <T>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};
const writeJson = (key: string, value: unknown) => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // full or blocked: the app still works, only without the copy
  }
};

class Store {
  session: Session | null = null;
  data: Data | null = null;
  online = typeof navigator === "undefined" ? true : navigator.onLine;
  /** The session ran out (or the device was signed out elsewhere): the data stays for reading. */
  signedOut = false;
  loading = false;
  lastError: string | null = null;
  ready = false;
  /** Increases with every change; React renders again on it. */
  version = 0;
  private readonly listeners = new Set<() => void>();
  private timer: ReturnType<typeof setInterval> | null = null;

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  private changed() {
    this.version++;
    for (const fn of this.listeners) fn();
  }

  async init() {
    // Data kept in another format (by an older app) is of no more use.
    try {
      for (const k of Object.keys(localStorage)) if (k.startsWith("speisekarte:data:") && !k.startsWith(`speisekarte:data:${DATA_FORMAT}:`)) localStorage.removeItem(k);
    } catch {
      // blocked: nothing kept anyway
    }
    this.session = readJson<Session>(SESSION_KEY);
    if (this.session) this.data = readJson<Data>(dataKey(this.session.workspaceId));
    this.ready = true;
    this.changed();
    window.addEventListener("online", () => [(this.online = true), void this.refresh()]);
    window.addEventListener("offline", () => [(this.online = false), this.changed()]);
    document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && void this.refresh());
    this.timer ??= setInterval(() => void this.refresh(), 60_000);
    if (this.session) {
      await this.refresh();
      void this.refreshMe();
    }
  }

  /** The account as the server has it now: whether its address is confirmed (S28), its businesses. */
  async refreshMe() {
    try {
      const me = await api<{ user: User; demo?: boolean; workspaces: Membership[] }>("/api/me");
      if (!this.session) return;
      this.session = { ...this.session, user: me.user, workspaces: me.workspaces, ...(me.demo ? { demo: true } : {}) };
      writeJson(SESSION_KEY, this.session);
      this.changed();
    } catch {
      // not needed to work
    }
  }

  /** The own account changed (an address saved or confirmed). */
  setUser(user: User) {
    if (!this.session) return;
    this.session = { ...this.session, user };
    writeJson(SESSION_KEY, this.session);
    this.changed();
  }

  /** What this device kept of the business goes; the next load fetches all of it (Crash.tsx). */
  async forget() {
    if (this.session) writeJson(dataKey(this.session.workspaceId), null);
    this.data = null;
  }

  /** After signing in: which business this device shows. */
  async choose(signedIn: SignedIn, workspaceId: string) {
    const changedBusiness = this.session?.workspaceId !== workspaceId;
    this.session = { user: signedIn.user, workspaces: signedIn.workspaces, workspaceId };
    this.signedOut = false;
    if (changedBusiness) this.data = readJson<Data>(dataKey(workspaceId));
    writeJson(SESSION_KEY, this.session);
    this.changed();
    await this.refresh(true);
    await this.refreshMe();
  }

  signedOutElsewhere() {
    if (!this.signedOut) {
      this.signedOut = true;
      this.changed();
    }
  }

  async signOut() {
    await api("/api/logout", { body: {} }, null).catch(() => undefined);
    if (this.session) writeJson(dataKey(this.session.workspaceId), null);
    writeJson(SESSION_KEY, null);
    this.session = null;
    this.data = null;
    this.signedOut = false;
    this.changed();
  }

  private queue: Promise<void> = Promise.resolve();

  /**
   * Fetches the data when it moved (or always with `full`). Calls wait for each other, so a change
   * made while a fetch runs is always followed by one that sees it.
   */
  refresh(full = false): Promise<void> {
    const run = this.queue.then(() => this.load(full));
    this.queue = run.catch(() => undefined);
    return run;
  }

  private async load(full: boolean): Promise<void> {
    if (!this.session || this.signedOut) return;
    this.loading = true;
    try {
      const since = !full && this.data ? `?since=${this.data.version}` : "";
      const res = await api<Data | { unchanged: true; version: number }>(`/api/data${since}`);
      if (!("unchanged" in res)) {
        this.data = res;
        writeJson(dataKey(this.session.workspaceId), res);
      }
      this.online = true;
      this.lastError = null;
    } catch (err) {
      if (err instanceof ApiError && err.code === "offline") this.online = false;
      else if (!(err instanceof ApiError && err.status === 401)) this.lastError = messageOf(err);
    } finally {
      this.loading = false;
      this.changed();
    }
  }

  /** A change on the server, then the new state. */
  async change<T>(fn: () => Promise<T>): Promise<T> {
    const out = await fn();
    await this.refresh();
    return out;
  }

  get role(): Role {
    return this.data?.role ?? this.session?.workspaces.find((w) => w.id === this.session?.workspaceId)?.role ?? "viewer";
  }

  get workspaceName(): string {
    return this.data?.workspace ?? this.session?.workspaces.find((w) => w.id === this.session?.workspaceId)?.name ?? "";
  }

  renamed(name: string) {
    if (this.session) {
      this.session = { ...this.session, workspaces: this.session.workspaces.map((w) => (w.id === this.session!.workspaceId ? { ...w, name } : w)) };
      writeJson(SESSION_KEY, this.session);
    }
    void this.refresh(true);
  }
}

export const store = new Store();

/** Renders again whenever the store changes. */
export function useStore() {
  useSyncExternalStore(store.subscribe, () => store.version);
  return store;
}

/** The texts by id (S14), built once per state of the data. */
let textsCache: { data: Data | null; map: Map<string, CardText> } = { data: null, map: new Map() };
export function textsById(data: Data | null): Map<string, CardText> {
  if (textsCache.data !== data) textsCache = { data, map: new Map((data?.texts ?? []).map((t) => [t.id, t])) };
  return textsCache.map;
}

/** The dishes by id, built once per state of the data. */
let byIdCache: { data: Data | null; map: Map<string, Dish> } = { data: null, map: new Map() };
export function dishesById(data: Data | null): Map<string, Dish> {
  if (byIdCache.data !== data) byIdCache = { data, map: new Map((data?.dishes ?? []).map((d) => [d.id, d])) };
  return byIdCache.map;
}
